import React from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import _ from 'lodash'
import Icon from 'components/common/Icon'
import { trackValue } from 'utils.js'
import NumberInput from 'components/common/NumberInput'
import { MAPBOX_API_KEY, MODE_OF_TRANSPORTATION, ISOCHRONE_SMOOTH_ITERATIONS, travelTime } from '../../../constants'
import axios from 'axios'
import polygonSmooth from '@turf/polygon-smooth'
import ColorPicker from '../common/ColorPicker'
import PaneHeader from './PaneHeader'
import LocationCoordsField from './LocationCoordsField'
import MapInstruction from '../common/MapInstruction'
import CustomDropdown from '../../common/CustomDropdown'

const IsochronePane = ({
  isochrone,
  group,
  uiStore,
  dataStore,
  selectionStore,
  onCancel,
  onSave,
  isQuickCreate,
  t
}) => {
  const handleSubmit = async () => {
    if (!isochrone.validate(group)) {
      return
    }
    if (onSave) {
      onSave()
    } else if (uiStore.quickCreate && uiStore.quickCreate.isOpen) {
      uiStore.closeQuickCreate()
    }
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `https://api.mapbox.com/isochrone/v1/mapbox/${isochrone.modeOfTransportation.toLowerCase()}/${isochrone.position.longitude},${isochrone.position.latitude}?contours_minutes=${isochrone.travelTime}&polygons=true&denoise=1&access_token=${MAPBOX_API_KEY}`,
      headers: {
        Accept: '*/*',
        'Accept-Language': 'en-US,en;q=0.9,ur;q=0.8',
        Connection: 'keep-alive',
        Referer: 'https://docs.mapbox.com/',
        'Sec-Fetch-Dest': 'empty',
        'Sec-Fetch-Mode': 'cors',
        'Sec-Fetch-Site': 'same-site'
      }
    }
    try {
      const response = await axios.request(config)
      const smoothedData = polygonSmooth(response.data, { iterations: ISOCHRONE_SMOOTH_ITERATIONS })
      isochrone.region = smoothedData

      if (isochrone.isUpdating) {
        await uiStore.updateGeopolygon(group, isochrone, smoothedData.features[0])
      } else {
        await uiStore.addGeopolygonIsoChrone(group, isochrone, smoothedData)
      }
    } catch (err) {
      console.warn(err)
    }
  }

  const handleEdit = () => {
    isochrone.initial = true
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    if (uiStore.quickCreate && uiStore.quickCreate.isOpen) {
      uiStore.closeQuickCreate()
      return
    }
    if (isochrone.isUpdating) {
      selectionStore.select(
        _.find(group.geopolygons, { id: isochrone.id }), { data: { group } }, false, true
      )
    } else {
      selectionStore.clear()
    }
  }

  const paneTitle = isochrone.isUpdating
    ? t('ITEM.EDIT_ISOCHRONE', { defaultValue: 'Edit isochrone' })
    : t('ITEM.CREATE_ISOCHRONE', { defaultValue: 'Isochrone' })

  const submitBtnText = isochrone.isUpdating
    ? t('GLOBAL.CONFIRM_SAVE', { defaultValue: 'Save isochrone' })
    : t('ITEM.CREATE_ISOCHRONE_BTN', { defaultValue: 'Create isochrone' })

  if (isQuickCreate) {
    return (
      <div className='line-pane'>
        <section>
          <h2>{isochrone.isUpdating ? t('ITEM.EDIT_ISOCHRONE', { defaultValue: 'Edit isochrone' }) : t('ITEM.CREATE_ISOCHRONE', { defaultValue: 'Create isochrone' })}</h2>
        </section>
        <form>
          <section className='spaced-children'>
            <label>
              <span>{t('GLOBAL.NAME')}</span>
              <input type='text' {...trackValue(isochrone, 'name')} autoFocus />
              <span className='invalid'>{isochrone.invalid.get('name')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.LONGITUDE')}</span>
              <NumberInput
                min={-180}
                max={180}
                value={isochrone.position.longitude}
                inputValue={isochrone.position.longitude}
                onChange={value => (isochrone.position.longitude = value)}
              />
              <span className='invalid'>{isochrone.invalid.get('longitude')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.LATITUDE')}</span>
              <NumberInput
                min={-90}
                max={90}
                value={isochrone.position.latitude}
                inputValue={isochrone.position.latitude}
                onChange={value => (isochrone.position.latitude = value)}
              />
              <span className='invalid'>{isochrone.invalid.get('latitude')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.MODE_OF_TRANSPORTATION')}</span>
              <select {...trackValue(isochrone, 'modeOfTransportation')}>
                {MODE_OF_TRANSPORTATION.map((elem) => (<option key={elem}>{elem}</option>))}
              </select>
            </label>
            <label>
              <span>{t('GLOBAL.TRAVEL_TIME')}</span>
              <input
                type='number'
                value={isochrone.travelTime}
                onChange={(e) => {
                  const newValue = e.target.value
                  isochrone.travelTime = newValue
                  if (newValue && (newValue < 1 || newValue > 60)) {
                    isochrone.enableSubmit = false
                  } else {
                    isochrone.enableSubmit = true
                  }
                }}
                placeholder='Enter a number (1-60)'
              />
              {
                !isochrone.enableSubmit && <span style={{ color: 'red' }}>Please enter a value between 1 and 60 </span>
              }
            </label>
            {
              !isochrone.isUpdating && (
                <div className='line-pane__picker'>
                  <ColorPicker
                    onClick={(idx) => { isochrone.color_id = idx }}
                    selectedId={isochrone.color_id}
                    hasTransparencyOption
                    transparency={isochrone.transparency}
                    onTransparencyChange={(val) => { isochrone.transparency = val }}
                  />
                </div>
              )
            }
          </section>
          <section>
            <div className='centered-button-row'>
              <button
                type='button'
                onClick={handleCancel}
              >
                <Icon id='fa-times' />
                <span>{t('GLOBAL.CANCEL')}</span>
              </button>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={!isochrone.enableSubmit}
              >
                <Icon id='fa-check' />
                <span>{isochrone.isUpdating ? t('GLOBAL.CONFIRM_SAVE') : t('GLOBAL.CONFIRM_ADD')}</span>
              </button>
            </div>
          </section>
        </form>
      </div>
    )
  }

  return (
    <div className='line-pane'>
      <PaneHeader
        icon={travelTime}
        title={paneTitle}
        onClose={handleCancel}
      />

      {(!isochrone.isUpdating && (!isochrone.position.latitude || !isochrone.position.longitude)) && (
        <MapInstruction>{t('ITEM.POINT_START', { defaultValue: 'Click on the map to place the point' })}</MapInstruction>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
        <section className='spaced-children'>
          <label>
            <span>{t('GLOBAL.NAME', { defaultValue: 'Name' })}</span>
            <input
              type='text'
              placeholder={t('ITEM.ISOCHRONE_NAME_PLACEHOLDER', { defaultValue: 'e.g. Market Street drive time' })}
              {...trackValue(isochrone, 'name')}
              autoFocus
            />
            <span className='invalid'>{isochrone.invalid.get('name')}</span>
          </label>

          <LocationCoordsField
            position={isochrone.position}
            onLatitudeChange={value => {
              isochrone.position.latitude = value
              isochrone.initial = false
            }}
            onLongitudeChange={value => {
              isochrone.position.longitude = value
              isochrone.initial = false
            }}
            invalidLatitude={isochrone.invalid.get('latitude')}
            invalidLongitude={isochrone.invalid.get('longitude')}
            t={t}
          />

          <label>
            <span>{t('GLOBAL.MODE_OF_TRANSPORTATION', { defaultValue: 'Mode of transport' })}</span>
            <CustomDropdown
              value={isochrone.modeOfTransportation}
              onChange={(val) => {
                isochrone.modeOfTransportation = val
              }}
              options={MODE_OF_TRANSPORTATION}
            />
          </label>

          <label>
            <span>{t('GLOBAL.TRAVEL_TIME', { defaultValue: 'Travel time' })}</span>
            <div className='input-with-unit'>
              <input
                type='number'
                value={isochrone.travelTime}
                onChange={(e) => {
                  const newValue = e.target.value
                  isochrone.travelTime = newValue
                  if (newValue && (newValue < 1 || newValue > 60)) {
                    isochrone.enableSubmit = false
                  } else {
                    isochrone.enableSubmit = true
                  }
                }}
                placeholder='Enter a number (1-60)'
              />
              <span className='input-with-unit__unit'>
                {t('GLOBAL.MINUTES', { defaultValue: 'minutes' })}
              </span>
            </div>
            {
              !isochrone.enableSubmit && <span className='invalid'>Please enter a value between 1 and 60</span>
            }
          </label>

          {
            !isochrone.isUpdating && (
              <div className='line-pane__picker'>
                <ColorPicker
                  onClick={(idx) => { isochrone.color_id = idx }}
                  selectedId={isochrone.color_id}
                  hasTransparencyOption
                  transparency={isochrone.transparency}
                  onTransparencyChange={(val) => { isochrone.transparency = val }}
                />
              </div>
            )
          }
        </section>

        {
          !isochrone.initial && (
            <div>
              <button
                type='button'
                className='finish-line edit-buffer-button'
                onClick={handleEdit}
              >
                <span>{t('ITEM.EDIT_POINT')}</span>
              </button>
            </div>
          )
        }

        <section>
          <div className='centered-button-row'>
            <button
              type='button'
              onClick={handleCancel}
            >
              <span>{t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}</span>
            </button>
            <button
              type='button'
              onClick={handleSubmit}
              disabled={!isochrone.enableSubmit}
            >
              <span>{submitBtnText}</span>
            </button>
          </div>
        </section>
      </form>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'dataStore', 'selectionStore')(
  observer(IsochronePane)
))
